import type { CmsSlide, SlideShape } from '@erp/shared';
import { GripVertical, ImageOff, Images, Link as LinkIcon, Pencil, Sparkles, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { Badge, Button, confirmDialog, EmptyState, FormField } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { deleteAttachment, fetchAttachmentObjectUrl, isImageMime, uploadAttachment } from '../../lib/attachments.js';
import { useAuthStore } from '../../lib/authStore.js';
import { moduleEnabled } from '../../lib/modules.js';
import { useEnabledModules } from '../../lib/modulesStore.js';
import { AiImageDialog } from '../items/AiImageDialog.js';

/**
 * Homepage slider / banners. Each slide is a banner image (+ optional title and
 * link) shown on the storefront home above the hero. Reuses the generic
 * attachments API (entityType='cms_slide') for the image.
 */
export function CmsSlidesPage(): JSX.Element {
  const { t } = useTranslation('cms');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('cms.write');
  const [slides, setSlides] = useState<CmsSlide[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<CmsSlide | null>(null);
  const [photoVersion, setPhotoVersion] = useState(0);

  const reload = async (): Promise<void> => {
    const r = await api().get<{ items: CmsSlide[] }>('/cms/slides');
    setSlides(r.data.items);
  };
  useEffect(() => { void reload(); }, []);

  const onDelete = async (s: CmsSlide): Promise<void> => {
    if (!(await confirmDialog({ message: t('slides.confirmDelete', { defaultValue: 'Delete this slide?' }), tone: 'danger' }))) return;
    try { await api().delete(`/cms/slides/${s.id}`); await reload(); } catch { toast.error(tc('errors:server')); }
  };
  const onToggle = async (s: CmsSlide): Promise<void> => {
    try { await api().put(`/cms/slides/${s.id}`, { isActive: !s.isActive }); await reload(); } catch { toast.error(tc('errors:server')); }
  };

  if (!slides) return <Card className="p-6"><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-40 w-full" /></Card>;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('slides.title', { defaultValue: 'Homepage slider' })}</h1>
          <p className="mt-1 text-sm text-fg-muted">
            {t('slides.description', { defaultValue: 'Banner images shown at the top of your storefront home. Add a link to make a banner clickable.' })}
          </p>
        </div>
        {canWrite && !showCreate && !editing && (
          <Button onClick={() => setShowCreate(true)}>{t('slides.add', { defaultValue: 'Add slide' })}</Button>
        )}
      </header>

      {showCreate && <SlideForm onCancel={() => setShowCreate(false)} onSaved={async () => { setShowCreate(false); setPhotoVersion((v) => v + 1); await reload(); }} />}
      {editing && <SlideForm initial={editing} onCancel={() => setEditing(null)} onSaved={async () => { setEditing(null); setPhotoVersion((v) => v + 1); await reload(); }} />}

      {slides.length === 0 && !showCreate ? (
        <EmptyState
          icon={<Images className="h-8 w-8" aria-hidden />}
          title={t('slides.empty', { defaultValue: 'No slides yet' })}
          description={t('slides.emptyDescription', { defaultValue: 'Add a banner image to feature on your storefront home.' })}
          action={canWrite ? <Button size="sm" onClick={() => setShowCreate(true)}>{t('slides.add', { defaultValue: 'Add slide' })}</Button> : undefined}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {slides.map((s) => (
            <Card key={s.id} className={`flex items-center gap-4 p-3 ${s.isActive ? '' : 'opacity-60'}`}>
              <GripVertical className="h-4 w-4 flex-none text-fg-subtle" aria-hidden />
              <SlideThumb slide={s} version={photoVersion} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold">{s.title || <span className="text-fg-muted">{t('slides.noTitle', { defaultValue: '(no title)' })}</span>}</div>
                {s.link && <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-fg-muted"><LinkIcon className="h-3 w-3" aria-hidden />{s.link}</div>}
                <div className="mt-1 flex items-center gap-1.5 text-xs text-fg-subtle">
                  <span>{t('slides.sort', { defaultValue: 'Sort' })}: {s.sort}</span>
                  <span aria-hidden>·</span>
                  <span>{s.shape === 'square' ? t('slides.shapeSquare', { defaultValue: 'Square tile' }) : t('slides.shapeRect', { defaultValue: 'Full-width banner' })}</span>
                </div>
              </div>
              <Badge tone={s.isActive ? 'success' : 'neutral'}>{t(`status.${s.isActive ? 'active' : 'inactive'}`, { defaultValue: s.isActive ? 'Active' : 'Inactive' })}</Badge>
              {canWrite && (
                <div className="flex flex-none gap-1">
                  <Button variant="ghost" size="sm" onClick={() => void onToggle(s)} title={s.isActive ? t('slides.deactivate', { defaultValue: 'Hide' }) : t('slides.activate', { defaultValue: 'Show' })}>
                    {s.isActive ? t('slides.hide', { defaultValue: 'Hide' }) : t('slides.show', { defaultValue: 'Show' })}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setShowCreate(false); setEditing(s); window.scrollTo({ top: 0, behavior: 'smooth' }); }} aria-label={tc('actions.edit')} title={tc('actions.edit')}>
                    <Pencil className="h-4 w-4" aria-hidden />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => void onDelete(s)} aria-label={tc('actions.delete')} title={tc('actions.delete')}>
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function SlideThumb({ slide, version }: { slide: CmsSlide; version: number }): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!slide.imageAttachmentId) { setUrl(null); return undefined; }
    let active = true; let made: string | null = null;
    void fetchAttachmentObjectUrl(slide.imageAttachmentId).then((u) => { if (active) { made = u; setUrl(u); } else URL.revokeObjectURL(u); }).catch(() => undefined);
    return () => { active = false; if (made) URL.revokeObjectURL(made); };
  }, [slide.imageAttachmentId, version]);
  return (
    <div className="grid h-14 w-24 flex-none place-items-center overflow-hidden rounded-md border border-border bg-bg-subtle">
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <ImageOff className="h-5 w-5 text-fg-muted" aria-hidden />}
    </div>
  );
}

function SlideForm({ initial, onCancel, onSaved }: { initial?: CmsSlide; onCancel: () => void; onSaved: () => void | Promise<void> }): JSX.Element {
  const { t } = useTranslation('cms');
  const { t: tc } = useTranslation();
  const isEdit = !!initial;
  const [title, setTitle] = useState(initial?.title ?? '');
  const [link, setLink] = useState(initial?.link ?? '');
  const [sort, setSort] = useState(String(initial?.sort ?? 0));
  const [shape, setShape] = useState<SlideShape>(initial?.shape ?? 'rectangle');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);
  // AI banner generation — only when the imagegen (Nano Banana) module is on.
  const tenantId = useAuthStore((s) => s.tenant?.id ?? null);
  const imagegenEnabled = moduleEnabled(useEnabledModules(tenantId), 'imagegen');

  useEffect(() => {
    if (!initial?.imageAttachmentId) return undefined;
    let active = true; let made: string | null = null;
    void fetchAttachmentObjectUrl(initial.imageAttachmentId).then((u) => { if (active) { made = u; setPreview(u); } else URL.revokeObjectURL(u); }).catch(() => undefined);
    return () => { active = false; if (made) URL.revokeObjectURL(made); };
  }, [initial?.imageAttachmentId]);
  useEffect(() => () => { if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current); }, []);

  const pickFile = (f: File | null): void => {
    setFile(f);
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    if (f) { const u = URL.createObjectURL(f); previewUrlRef.current = u; setPreview(u); }
  };

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!isEdit && !file) { setError(t('slides.imageRequired', { defaultValue: 'Pick a banner image.' })); return; }
    setSaving(true); setError(null);
    try {
      const body = { title: title.trim() || null, link: link.trim() || null, sort: Number(sort) || 0, shape };
      let id = initial?.id;
      if (isEdit && initial) await api().put(`/cms/slides/${initial.id}`, body);
      else { const r = await api().post<CmsSlide>('/cms/slides', body); id = r.data.id; }
      // Upload the new image (if any) + point the slide at it.
      if (id && file) {
        // Replace the previous image so old attachments don't accumulate.
        if (initial?.imageAttachmentId) { try { await deleteAttachment(initial.imageAttachmentId); } catch { /* ignore */ } }
        const att = await uploadAttachment('cms_slide', id, file);
        if (isImageMime(att.mimeType)) await api().put(`/cms/slides/${id}`, { imageAttachmentId: att.id });
      }
      await onSaved();
    } catch (err) {
      setError((err as { response?: { data?: { code?: string } } }).response?.data?.code ? tc('errors:server') : tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">{isEdit ? t('slides.edit', { defaultValue: 'Edit slide' }) : t('slides.add', { defaultValue: 'Add slide' })}</h2>
      <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={submit} noValidate>
        <div className="md:col-span-2 flex items-center gap-4">
          <div className="grid h-20 w-36 flex-none place-items-center overflow-hidden rounded-lg border border-border bg-bg-subtle">
            {preview ? <img src={preview} alt="" className="h-full w-full object-cover" /> : <ImageOff className="h-7 w-7 text-fg-muted" aria-hidden />}
          </div>
          <div>
            <div className="text-sm font-medium">{t('slides.image', { defaultValue: 'Banner image' })}</div>
            <p className="mt-0.5 text-xs text-fg-muted">{t('slides.imageHint', { defaultValue: 'Wide banner recommended (e.g. 1600×600).' })}</p>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickFile(e.target.files?.[0] ?? null)} />
            <div className="mt-2 flex flex-wrap gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
                <Upload className="h-4 w-4" aria-hidden />{preview ? t('slides.changeImage', { defaultValue: 'Change' }) : t('slides.uploadImage', { defaultValue: 'Upload' })}
              </Button>
              {imagegenEnabled && (
                <Button type="button" variant="secondary" size="sm" onClick={() => setAiOpen(true)}>
                  <Sparkles className="h-4 w-4" aria-hidden />{t('slides.aiImage', { defaultValue: 'Generate with AI' })}
                </Button>
              )}
            </div>
          </div>
        </div>
        <FormField id="sl-title" label={t('slides.titleField', { defaultValue: 'Title (optional)' })}>
          <Input id="sl-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={191} />
        </FormField>
        <FormField id="sl-link" label={t('slides.link', { defaultValue: 'Link (optional)' })}>
          <Input id="sl-link" value={link} onChange={(e) => setLink(e.target.value)} placeholder="/s/your-store/shop" maxLength={500} />
        </FormField>
        <FormField id="sl-sort" label={t('slides.sort', { defaultValue: 'Sort' })}>
          <Input id="sl-sort" type="number" value={sort} onChange={(e) => setSort(e.target.value)} />
        </FormField>
        <div className="md:col-span-2 flex flex-col gap-1.5">
          <span className="text-sm font-medium">{t('slides.shapeLabel', { defaultValue: 'Layout' })}</span>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant={shape === 'rectangle' ? 'primary' : 'secondary'} size="sm" onClick={() => setShape('rectangle')} aria-pressed={shape === 'rectangle'}>
              {t('slides.shapeRect', { defaultValue: 'Full-width banner' })}
            </Button>
            <Button type="button" variant={shape === 'square' ? 'primary' : 'secondary'} size="sm" onClick={() => setShape('square')} aria-pressed={shape === 'square'}>
              {t('slides.shapeSquare', { defaultValue: 'Square tile' })}
            </Button>
          </div>
          <p className="text-xs text-fg-muted">
            {t('slides.shapeHint', { defaultValue: 'Full-width banners show one at a time; square tiles show several per row. Use a square image (e.g. 800×800) for square slides.' })}
          </p>
        </div>
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{isEdit ? tc('actions.save') : t('slides.add', { defaultValue: 'Add slide' })}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>
      {imagegenEnabled && (
        <AiImageDialog
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          name={title.trim() || t('slides.aiSeed', { defaultValue: 'store promotional banner' })}
          onUse={(blob) => pickFile(new File([blob], 'ai-slide.png', { type: blob.type || 'image/png' }))}
        />
      )}
    </Card>
  );
}
